import React, { createContext, useContext, useState, useEffect } from 'react'
import { auth } from '../services/firebase'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import api from '../services/api'
import toast from 'react-hot-toast'

const AuthContext = createContext()

// Check if Firebase is initialized
const isFirebaseAvailable = () => {
  return auth !== undefined && auth !== null
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(null)

  useEffect(() => {
    // If Firebase is not available, skip auth initialization
    if (!isFirebaseAvailable()) {
      console.warn('Firebase is not initialized. Auth features will be limited.')
      setLoading(false)
      return
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idToken = await user.getIdToken()
          setToken(idToken)
          // Set user from Firebase
          setCurrentUser({
            id: user.uid,
            email: user.email,
            name: user.displayName || 'User'
          })
          // Update auth header once
          api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`
        } catch (error) {
          console.error('Auth error:', error)
          setCurrentUser(null)
          setToken(null)
        }
      } else {
        setCurrentUser(null)
        setToken(null)
        // Remove auth header once
        delete api.defaults.headers.common['Authorization']
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const login = async (email, password) => {
    try {
      if (!isFirebaseAvailable()) {
        toast.error('Firebase is not configured. Please try again later.')
        throw new Error('Firebase not available')
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const idToken = await userCredential.user.getIdToken()

      const response = await api.post('/auth/login', { token: idToken })
      setCurrentUser(response.data)
      setToken(response.data.access_token)

      // Set authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`

      toast.success('Login successful!')
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed')
      throw error
    }
  }

  const register = async (email, password, fullName, phone) => {
    try {
      if (!isFirebaseAvailable()) {
        toast.error('Firebase is not configured. Please try again later.')
        throw new Error('Firebase not available')
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const idToken = await userCredential.user.getIdToken()

      // Update user profile
      await userCredential.user.updateProfile({
        displayName: fullName
      })

      // Call backend register endpoint with Firebase credentials
      const response = await api.post('/users/register', {
        email: email,
        name: fullName,
        firebaseUid: userCredential.user.uid
      })

      // Get auth token from backend
      const authResponse = await api.post('/auth/login', { token: idToken })
      setCurrentUser(authResponse.data)
      setToken(authResponse.data.access_token)

      // Set authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`

      toast.success('Registration successful!')
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed')
      throw error
    }
  }

  const loginWithGoogle = async () => {
    try {
      if (!isFirebaseAvailable()) {
        toast.error('Firebase is not configured. Please try again later.')
        throw new Error('Firebase not available')
      }

      const provider = new GoogleAuthProvider()
      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await userCredential.user.getIdToken()

      try {
        // Try to login first (user exists)
        const response = await api.post('/auth/login', { token: idToken })
        setCurrentUser(response.data)
        setToken(response.data.access_token)
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`
        toast.success('Google login successful!')
        return response.data
      } catch (error) {
        // If login fails, register the user
        if (error.response?.status === 404 || error.response?.status === 400) {
          try {
            const email = userCredential.user.email
            const fullName = userCredential.user.displayName || email.split('@')[0]

            // Register user
            const registerResponse = await api.post('/users/register', {
              email: email,
              name: fullName,
              firebaseUid: userCredential.user.uid
            })

            // Now login
            const authResponse = await api.post('/auth/login', { token: idToken })
            setCurrentUser(authResponse.data)
            setToken(authResponse.data.access_token)
            api.defaults.headers.common['Authorization'] = `Bearer ${authResponse.data.access_token}`
            toast.success('Google registration and login successful!')
            return authResponse.data
          } catch (registerError) {
            toast.error(registerError.response?.data?.detail || 'Failed to register Google account')
            throw registerError
          }
        }
        throw error
      }
    } catch (error) {
      console.error('Google login error:', error)
      toast.error(error.response?.data?.detail || 'Google login failed')
      throw error
    }
  }

  const logout = async () => {
    try {
      if (!isFirebaseAvailable()) {
        setCurrentUser(null)
        setToken(null)
        delete api.defaults.headers.common['Authorization']
        toast.success('Logged out successfully!')
        return
      }

      await signOut(auth)
      setCurrentUser(null)
      setToken(null)
      delete api.defaults.headers.common['Authorization']
      toast.success('Logged out successfully!')
    } catch (error) {
      toast.error('Logout failed')
      throw error
    }
  }

  const value = {
    currentUser,
    token,
    login,
    register,
    loginWithGoogle,
    logout,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
